// Lock screen overlay — first-run PIN setup, unlock, and forgot-PIN reset.
// Renders into a body-level overlay (#lock-overlay) above #app, so screens
// behind keep their state while interaction is blocked.

import { hasPin, setupPin, unlock, wipeAllData } from '../auth/session.js';
import { toast, esc } from './util.js';

const OVERLAY_ID = 'lock-overlay';

function getOrCreateOverlay() {
  let el = document.getElementById(OVERLAY_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = OVERLAY_ID;
    el.className = 'lock-overlay';
    el.hidden = true;
    document.body.appendChild(el);
  }
  return el;
}

export async function showLockScreen() {
  const overlay = getOrCreateOverlay();
  overlay.hidden = false;
  document.body.dataset.locked = 'true';
  if (await hasPin()) {
    renderUnlock(overlay);
  } else {
    renderFirstRun(overlay);
  }
}

export function hideLockScreen() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) overlay.hidden = true;
  document.body.dataset.locked = 'false';
}

// ---- First-run: pick a PIN ----

function renderFirstRun(overlay) {
  overlay.innerHTML = `
    <div class="lock-card">
      <h1>Set up MaxHealth</h1>
      <p>Pick a PIN to protect your data on this device. It encrypts every photo and journal entry.</p>
      <p class="muted small"><strong>Important:</strong> if both parents forget the PIN, the data cannot be recovered. Write it down somewhere safe.</p>
      <label class="field">
        <span>Choose a PIN (4–16 characters)</span>
        <input id="pin1" type="password" inputmode="numeric" autocomplete="new-password"
               minlength="4" maxlength="16" aria-label="Choose PIN" />
      </label>
      <label class="field">
        <span>Confirm PIN</span>
        <input id="pin2" type="password" inputmode="numeric" autocomplete="new-password"
               minlength="4" maxlength="16" aria-label="Confirm PIN" />
      </label>
      <button id="pin-create" class="btn btn--primary">Create PIN</button>
    </div>
  `;
  const pin1 = overlay.querySelector('#pin1');
  const pin2 = overlay.querySelector('#pin2');
  const submit = overlay.querySelector('#pin-create');

  const tryCreate = async () => {
    const a = pin1.value;
    const b = pin2.value;
    if (a.length < 4) { toast('PIN must be at least 4 characters', { kind: 'warn' }); pin1.focus(); return; }
    if (a !== b)      { toast('PINs do not match',               { kind: 'warn' }); pin2.value = ''; pin2.focus(); return; }
    submit.disabled = true;
    submit.textContent = 'Creating…';
    try {
      await setupPin(a);
      // session 'unlock' event triggers hideLockScreen() from main.js
    } catch (err) {
      console.error(err);
      toast(err.message, { kind: 'error' });
      submit.disabled = false;
      submit.textContent = 'Create PIN';
    }
  };

  submit.addEventListener('click', tryCreate);
  pin2.addEventListener('keydown', (e) => { if (e.key === 'Enter') tryCreate(); });
  setTimeout(() => pin1.focus(), 50);
}

// ---- Subsequent launches: enter PIN ----

function renderUnlock(overlay) {
  overlay.innerHTML = `
    <div class="lock-card">
      <h1>MaxHealth</h1>
      <p>Enter your PIN.</p>
      <label class="field">
        <span class="sr-only">PIN</span>
        <input id="pin-input" type="password" inputmode="numeric" autocomplete="current-password"
               maxlength="16" aria-label="PIN" />
      </label>
      <button id="pin-submit" class="btn btn--primary">Unlock</button>
      <button id="pin-forgot" class="link-btn">Forgot PIN?</button>
    </div>
  `;
  const input = overlay.querySelector('#pin-input');
  const submit = overlay.querySelector('#pin-submit');
  const forgot = overlay.querySelector('#pin-forgot');

  const tryUnlock = async () => {
    const pin = input.value;
    if (!pin) return;
    submit.disabled = true;
    submit.textContent = 'Unlocking…';
    try {
      const ok = await unlock(pin);
      if (!ok) {
        input.value = '';
        toast('Wrong PIN', { kind: 'error' });
      }
      // success → 'unlock' event in main.js hides the overlay.
    } catch (err) {
      console.error(err);
      toast(err.message, { kind: 'error' });
    } finally {
      submit.disabled = false;
      submit.textContent = 'Unlock';
      input.focus();
    }
  };

  submit.addEventListener('click', tryUnlock);
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') tryUnlock(); });
  forgot.addEventListener('click', () => renderForgotConfirm(overlay));
  setTimeout(() => input.focus(), 50);
}

// ---- Forgot PIN → wipe everything and start over ----

function renderForgotConfirm(overlay) {
  overlay.innerHTML = `
    <div class="lock-card">
      <h1>Reset device</h1>
      <p>There's no PIN recovery. The only way forward is to <strong>delete all data on this device</strong> and start over.</p>
      <p class="muted small">Photos and journal entries on this phone will be erased and cannot be recovered.</p>
      <button id="wipe-confirm" class="btn btn--danger">Delete all data and reset</button>
      <button id="wipe-cancel" class="link-btn">Cancel</button>
    </div>
  `;
  overlay.querySelector('#wipe-confirm').addEventListener('click', async () => {
    try {
      await wipeAllData();
      toast('Data deleted. Set a new PIN.');
      showLockScreen(); // re-renders as first-run
    } catch (err) {
      console.error(err);
      toast(err.message, { kind: 'error' });
    }
  });
  overlay.querySelector('#wipe-cancel').addEventListener('click', () => renderUnlock(overlay));
}
